import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Building2,
  ArrowLeft,
  Upload,
  QrCode,
  FileText,
  CreditCard,
  TrendingUp,
  Send,
  Receipt,
  Bell,
  User,
  Home,
  Search,
  MoreHorizontal,
  Eye,
  EyeOff,
  ChevronRight,
  Palette,
  MessageCircle,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_digital-bank-builder/artifacts/yj9xruy7_WhatsApp%20Image%202026-03-17%20at%2012.24.28.jpeg";
const WHATSAPP_LINK = "https://wa.me/5511921780586?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20como%20criar%20minha%20Fintech.";

const colorPresets = [
  { name: "hubaas Original", primary: "#06B6D4", secondary: "#22C55E" },
  { name: "Cyan & Laranja", primary: "#06B6D4", secondary: "#F97316" },
  { name: "Verde & Amarelo", primary: "#22C55E", secondary: "#F59E0B" },
  { name: "Laranja & Cyan", primary: "#F97316", secondary: "#06B6D4" },
  { name: "Azul Corporativo", primary: "#3B82F6", secondary: "#10B981" },
  { name: "Escuro Premium", primary: "#0A0A0A", secondary: "#06B6D4" },
];

export default function BankSimulator() {
  const [bankName, setBankName] = useState("Minha Fintech");
  const [primaryColor, setPrimaryColor] = useState("#06B6D4");
  const [secondaryColor, setSecondaryColor] = useState("#22C55E");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);
  const [showBalance, setShowBalance] = useState(true);
  const fileInputRef = useRef(null);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Arquivo muito grande. Máximo 2MB.");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setLogoUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const applyPreset = (preset) => {
    setPrimaryColor(preset.primary);
    setSecondaryColor(preset.secondary);
    toast.success(`Tema "${preset.name}" aplicado!`);
  };

  return (
    <div className="min-h-screen bg-black" data-testid="bank-simulator-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-secondary transition-colors" data-testid="back-home">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Voltar</span>
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="hubaas" className="w-10 h-10 rounded-lg object-contain" />
              <h1 className="font-heading font-bold text-lg text-white">Simulador de Fintech</h1>
            </div>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" data-testid="nav-whatsapp">
                <MessageCircle className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Especialista</span>
              </Button>
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Controls Panel */}
          <div className="space-y-6 order-2 lg:order-1">
            <Card className="bg-zinc-900 border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Building2 className="w-5 h-5 text-secondary" />
                  Identidade da Fintech
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="bankName" className="text-slate-300">Nome da Fintech</Label>
                  <Input
                    id="bankName"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder="Ex: Minha Fintech"
                    className="mt-2 bg-zinc-800 border-white/10 text-white placeholder:text-slate-500"
                    data-testid="bank-name-input"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Logo da Fintech</Label>
                  <div className="mt-2 flex items-center gap-4">
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="w-20 h-20 rounded-xl border-2 border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-secondary hover:bg-white/5 transition-all overflow-hidden"
                      data-testid="logo-upload-area"
                    >
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                      ) : (
                        <Upload className="w-8 h-8 text-slate-500" />
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                      data-testid="logo-file-input"
                    />
                    <div className="text-sm text-slate-400">
                      <p className="font-medium">Clique para fazer upload</p>
                      <p className="text-slate-500">PNG, JPG até 2MB</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Palette className="w-5 h-5 text-secondary" />
                  Cores do Aplicativo
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="primaryColor" className="text-slate-300">Cor Primária</Label>
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="color"
                        id="primaryColor"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        className="w-12 h-12 rounded-lg cursor-pointer border-0"
                        data-testid="primary-color-picker"
                      />
                      <Input
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        className="uppercase bg-zinc-800 border-white/10 text-white"
                        data-testid="primary-color-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="secondaryColor" className="text-slate-300">Cor Secundária</Label>
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="color"
                        id="secondaryColor"
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        className="w-12 h-12 rounded-lg cursor-pointer border-0"
                        data-testid="secondary-color-picker"
                      />
                      <Input
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        className="uppercase bg-zinc-800 border-white/10 text-white"
                        data-testid="secondary-color-input"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300 mb-3 block">Temas Pré-definidos</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {colorPresets.map((preset, index) => (
                      <button
                        key={index}
                        onClick={() => applyPreset(preset)}
                        className="p-3 rounded-xl border border-white/10 hover:border-secondary hover:bg-white/5 transition-all text-left"
                        data-testid={`color-preset-${index}`}
                      >
                        <div className="flex gap-1 mb-2">
                          <div className="w-6 h-6 rounded-full" style={{ backgroundColor: preset.primary }} />
                          <div className="w-6 h-6 rounded-full" style={{ backgroundColor: preset.secondary }} />
                        </div>
                        <p className="text-xs font-medium text-slate-300">{preset.name}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="block">
              <Button className="w-full bg-green-600 hover:bg-green-700 text-white py-6" data-testid="go-to-viability">
                <MessageCircle className="mr-2 w-5 h-5" />
                Fale com um Especialista
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>

          {/* Phone Preview */}
          <div className="order-1 lg:order-2 lg:sticky lg:top-24 lg:self-start">
            <div className="text-center mb-4">
              <p className="text-sm text-slate-500">Visualização em tempo real</p>
            </div>
            
            <div className="phone-mockup max-w-[340px] mx-auto" data-testid="phone-preview">
              <div className="phone-screen">
                {/* Status Bar */}
                <div className="flex items-center justify-between px-6 py-2 bg-zinc-900 text-white text-xs">
                  <span>9:41</span>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-2 bg-white rounded-sm" />
                  </div>
                </div>

                {/* App Header */}
                <div
                  className="p-6 text-white"
                  style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo" className="w-10 h-10 rounded-xl object-contain bg-white/20 p-1" />
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                          <Building2 className="w-5 h-5" />
                        </div>
                      )}
                      <div>
                        <p className="font-bold text-lg" data-testid="preview-bank-name">{bankName || "Minha Fintech"}</p>
                        <p className="text-xs opacity-80">Olá, João</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="p-2 rounded-full bg-white/10">
                        <Bell className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-white/10 rounded-2xl p-4 backdrop-blur-sm">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm opacity-80">Saldo disponível</p>
                      <button onClick={() => setShowBalance(!showBalance)} className="p-1">
                        {showBalance ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-3xl font-bold" data-testid="preview-balance">
                      {showBalance ? "R$ 12.450,00" : "••••••"}
                    </p>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="p-4 -mt-4 bg-zinc-900">
                  <div className="bg-zinc-800 rounded-2xl shadow-lg p-4">
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { icon: QrCode, label: "PIX" },
                        { icon: FileText, label: "Boleto" },
                        { icon: Send, label: "Transf." },
                        { icon: Receipt, label: "Pagar" },
                      ].map((item, i) => (
                        <button
                          key={i}
                          className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-white/5 transition-colors"
                        >
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: `${primaryColor}30` }}
                          >
                            <item.icon className="w-5 h-5" style={{ color: primaryColor }} />
                          </div>
                          <span className="text-xs text-slate-400 font-medium">{item.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Card Preview */}
                <div className="px-4 pb-2 bg-zinc-900">
                  <p className="text-sm font-semibold text-white mb-2">Meu Cartão</p>
                  <div
                    className="rounded-2xl p-4 text-white relative overflow-hidden"
                    style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }}
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="flex justify-between items-start mb-4">
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo" className="w-8 h-8 rounded object-contain bg-white/20 p-1" />
                      ) : (
                        <Building2 className="w-6 h-6" />
                      )}
                      <CreditCard className="w-6 h-6" />
                    </div>
                    <p className="font-mono text-sm opacity-80 mb-1">•••• •••• •••• 4242</p>
                    <p className="text-xs font-medium">{bankName || "Minha Fintech"}</p>
                  </div>
                </div>

                {/* Transactions */}
                <div className="px-4 pb-4 bg-zinc-900">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-white">Últimas transações</p>
                    <button className="text-xs font-medium" style={{ color: primaryColor }}>Ver todas</button>
                  </div>
                  <div className="space-y-2">
                    {[
                      { name: "PIX Recebido", desc: "João Silva", value: "+ R$ 500,00", positive: true },
                      { name: "Compra", desc: "Supermercado", value: "- R$ 156,90", positive: false },
                      { name: "Assinatura", desc: "Netflix", value: "- R$ 55,90", positive: false },
                    ].map((tx, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-zinc-800 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: tx.positive ? `${secondaryColor}20` : `${primaryColor}20` }}
                          >
                            {tx.positive ? (
                              <TrendingUp className="w-5 h-5" style={{ color: secondaryColor }} />
                            ) : (
                              <Receipt className="w-5 h-5" style={{ color: primaryColor }} />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">{tx.name}</p>
                            <p className="text-xs text-slate-500">{tx.desc}</p>
                          </div>
                        </div>
                        <p className={`text-sm font-semibold ${tx.positive ? 'text-green-500' : 'text-white'}`}>
                          {tx.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bottom Nav */}
                <div className="border-t border-white/10 p-2 bg-zinc-900">
                  <div className="flex items-center justify-around">
                    {[
                      { icon: Home, label: "Início", active: true },
                      { icon: Search, label: "Buscar", active: false },
                      { icon: CreditCard, label: "Cartões", active: false },
                      { icon: User, label: "Perfil", active: false },
                    ].map((item, i) => (
                      <button key={i} className="flex flex-col items-center gap-1 p-2">
                        <item.icon
                          className="w-5 h-5"
                          style={{ color: item.active ? primaryColor : '#64748b' }}
                        />
                        <span
                          className="text-xs font-medium"
                          style={{ color: item.active ? primaryColor : '#64748b' }}
                        >
                          {item.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
